import React from 'react';
import { Box, Button, Container, Paper, Typography, IconButton, Link as MuiLink, useTheme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { useCookieConsent } from './CookieConsentContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { getPrimaryButtonStyles, getSecondaryButtonStyles, textMeasure } from '../../shared/styles';

const contentUseCase = new ContentUseCase();

export const CookieBanner: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const { setCookieConsent, showBanner } = useCookieConsent();
  const { cookieBanner } = contentUseCase.getLegalContent(language);

  const handleAccept = () =>
    setCookieConsent('accepted', { preferences: true, theme: true, analytics: true });
  const handleDecline = () => setCookieConsent('declined');

  if (!showBanner) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      role="region"
      aria-label={cookieBanner.title}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.snackbar,
        backgroundColor: 'background.paper',
        borderRadius: 0,
        borderTop: '2px solid',
        borderColor: 'primary.main',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ py: 3, position: 'relative' }}>
          <IconButton
            onClick={handleDecline}
            aria-label={cookieBanner.close}
            sx={{ position: 'absolute', right: 0, top: 4 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" component="h2" gutterBottom sx={{ pr: 5 }}>
            {cookieBanner.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ...textMeasure }}>
            {cookieBanner.description}{' '}
            <MuiLink component={Link} to={lp('/cookies')} sx={{ color: 'primary.dark' }}>
              {cookieBanner.cookiePolicy}
            </MuiLink>
            {' · '}
            <MuiLink component={Link} to={lp('/privacy')} sx={{ color: 'primary.dark' }}>
              {cookieBanner.privacyPolicy}
            </MuiLink>
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button onClick={handleAccept} sx={{ ...getPrimaryButtonStyles(theme), py: 1, px: 3 }}>
              {cookieBanner.acceptAll}
            </Button>
            <Button onClick={handleDecline} sx={{ ...getSecondaryButtonStyles(theme), py: 1, px: 3 }}>
              {cookieBanner.declineAll}
            </Button>
            <Button
              component={Link}
              to={lp('/cookie-settings')}
              sx={{ ...getSecondaryButtonStyles(theme), py: 1, px: 3 }}
            >
              {cookieBanner.settings}
            </Button>
          </Box>
        </Box>
      </Container>
    </Paper>
  );
};
